import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getLookupData } from 'modules/api/lookups';
import Chip, { Color, Width } from 'lib/Chip';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import Button, { Color as ButtonColor } from 'lib/Button';
import AsyncSelect from 'lib/AsyncSelect';
import { getIsReseller } from 'store/publisher/selectors';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import multiPublisherRequest from 'modules/api/multiPublisherRequest';

import { CodeNameModel, FileList } from 'components/common/types';

import { getEnvironmentId } from 'store/environment/selectors';
import { cancelFunctions } from 'utils/cancelFunctions';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import VisualUnitFilesUpload from './VisualUnitFilesUpload';

interface VisualUnitsProps {
  isReadOnly: boolean;
  visualUnitCodes: RouteFrameCode[] | undefined;
  onChangeVisualUnitCodes: (code: RouteFrameCode[] | undefined) => void;
  visualUnitFiles: FileList[] | undefined;
  onChangeVisualUnitFiles: (files: FileList[]) => void;
}

const VisualUnits = ({
  isReadOnly,
  visualUnitCodes,
  onChangeVisualUnitCodes,
  visualUnitFiles,
  onChangeVisualUnitFiles,
}: VisualUnitsProps): React.ReactElement => {
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);
  const isReseller = useSelector(getIsReseller);
  const environmentId = useSelector(getEnvironmentId);
  const [visualUnitCodesWithLabels, setVisualUnitCodesWithLabels] = useState<(RouteFrameCode & { label: string })[]>(
    [],
  );

  const fetchVisualUnitCodes = async (dropdownName: string, lookupCode: string): Promise<RouteFrameCode[]> => {
    const requestArguments = { cancelFunctions, lookupURLName: dropdownName, query: '', lookupCode };
    try {
      if (isReseller) {
        const response = (await multiPublisherRequest(environmentId, resellerMediaOwners, getLookupData, {
          ...requestArguments,
        })) as { publisher: string; code: string }[];

        return response?.map(({ publisher, code }: { publisher: string; code: string }) => ({
          code,
          name: code,
          mediaOwner: publisher,
          label: `${publisher} : ${code}`,
          include: true,
        }));
      }
      const response = await getLookupData({ ...requestArguments });
      return response.map(({ code }: { code: string }) => ({
        code,
        name: code,
        mediaOwner: environmentId,
        label: code,
        include: true,
      }));
    } catch {
      return [];
    }
  };

  const onIncludeExcludeChange = (updatedItem: CodeNameModel): void => {
    onChangeVisualUnitCodes(
      visualUnitCodes?.map((visualUnitCode: RouteFrameCode) =>
        visualUnitCode.code === updatedItem.code
          ? { ...visualUnitCode, include: updatedItem.include !== false }
          : visualUnitCode,
      ) ?? [],
    );
  };

  useEffect(() => {
    setVisualUnitCodesWithLabels(
      visualUnitCodes?.map((visualUnit) => ({
        ...visualUnit,
        label: isReseller ? `${visualUnit.mediaOwner} : ${visualUnit.code}` : visualUnit.code,
      })) || [],
    );
  }, [visualUnitCodes]);

  return (
    <div className="grid grid-cols-2 gap-x-7 xl:gap-x-24 px-12 py-6" data-test-id="visualUnits">
      <div>
        <p className="mb-2 sub-header-base text-neutral-950-opacity-60">Visual units ID</p>
        <div className="mb-1.5">
          <AsyncSelect
            name="visualunitcodes"
            dataTestId="visualunitcodes"
            loadData={fetchVisualUnitCodes}
            selectedValue={visualUnitCodesWithLabels}
            placeholder="VU code"
            onSelect={(_: string, value: RouteFrameCode[]) =>
              onChangeVisualUnitCodes(value.map((item) => ({ ...item, include: item.include !== false })))
            }
            isClearable={false}
            multiple
            isMultiValueVisible={false}
            valueKey="code"
            textKey="label"
            isDisabled={isReadOnly}
          />
        </div>
        {visualUnitCodes?.length ? (
          <div data-test-id="visualunitcodes-chips" className="flex flex-col gap-2 mt-2">
            {visualUnitCodesWithLabels.map((item) => (
              <div key={item.code} aria-label={item.name} className="flex justify-between items-center">
                <Chip
                  label={item.label}
                  color={Color.PRIMARY}
                  width={Width.FULL}
                  truncateLabel={false}
                  onRemove={() => onChangeVisualUnitCodes(visualUnitCodes.filter(({ code }) => code !== item.code))}
                  isDisabled={isReadOnly}
                />
                <IncludeExcludeToggle
                  option={{ ...item, name: item.label }}
                  onIncludeExcludeChange={onIncludeExcludeChange}
                  isAlwaysVisible
                  isSelected={item.include !== undefined}
                  isOptionNameSelectable={false}
                  isHovered={false}
                  isDeselectAllowed={false}
                  isDisabled={isReadOnly}
                />
              </div>
            ))}
            {!isReadOnly && (
              <Button
                classNames="w-fit"
                label="Clear"
                color={ButtonColor.SECONDARY}
                onClick={() => onChangeVisualUnitCodes([])}
              />
            )}
          </div>
        ) : null}
      </div>
      <VisualUnitFilesUpload
        isReadOnly={isReadOnly}
        visualUnitFiles={visualUnitFiles}
        onChangeVisualUnitFiles={onChangeVisualUnitFiles}
      />
    </div>
  );
};

export default VisualUnits;
